/**
 * Formatting shared by the user list and the user detail panels.
 *
 * These were inline in the old 1,300-line users page. They are here rather than in a panel because
 * the list and at least two panels each need some of them, and a second copy of `describeTerm` is
 * how two screens start disagreeing about when a subscription lapses.
 */

import type { CreditStatus } from '@api/types/subscription'

/** The roles the portal can assign. Matches the server's seeded role set. */
// RegionalModerator reviews trail routes inside the regions assigned below the roles; nothing else.
export const AVAILABLE_ROLES = ['Admin', 'Moderator', 'RegionalModerator', 'User']

/** Dates arrive as ISO strings and are frequently absent (never logged in, no updated stamp). */
export function formatDate(dateStr?: string): string {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString()
}

/** Same, but day-only — for anything shown next to a lot of other text. */
export function formatDay(dateStr?: string): string {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
}

// The API registers no global JsonStringEnumConverter, so SubscriptionStatus arrives on the wire
// as its ordinal even though the DTO types it as a union. Indexes match SubscriptionEnums.cs.
const SUBSCRIPTION_STATUS_NAMES = ['Trial', 'Active', 'Expired', 'Cancelled', 'PastDue', 'Paused', 'Free']

export function subscriptionStatusName(status: string | number): string {
  return typeof status === 'number' ? SUBSCRIPTION_STATUS_NAMES[status] ?? String(status) : status
}

// Credit status arrives the same way, as an ordinal. Indexes match CreditStatus on the server.
const CREDIT_STATUS_NAMES: CreditStatus[] = ['Pending', 'Redeemed', 'Expired', 'Revoked']

export function creditStatusName(status: CreditStatus | number): CreditStatus {
  return typeof status === 'number' ? CREDIT_STATUS_NAMES[status] ?? 'Pending' : status
}

/**
 * The free tier's period end is a +100 year sentinel, and "no expiry" grants reuse it, so anything
 * that far out is reported as open-ended rather than as a date no one meant to set.
 */
export function describeTerm(currentPeriodEnd: string, isFreeTier: boolean): string {
  if (isFreeTier) return 'Free tier — does not expire'

  const end = new Date(currentPeriodEnd)
  const tenYearsOut = new Date()
  tenYearsOut.setFullYear(tenYearsOut.getFullYear() + 10)
  if (end > tenYearsOut) return 'No expiry — will never lapse on its own'

  return end.getTime() < Date.now()
    ? `Lapsed ${end.toLocaleDateString()} — awaiting downgrade`
    : `Reverts to free tier on ${end.toLocaleDateString()}`
}

/** Every panel surfaces its own failures, and they all unwrap the error the same way. */
export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

/** What to call a user in a dialog title or a confirmation sentence. */
export function userLabel(user: { displayName?: string; email?: string; id?: string }): string {
  return user.displayName || user.email || user.id || 'this user'
}

/**
 * Like `errorMessage`, but prefers the server's own explanation. The API client rejects with the
 * raw axios error, whose `message` is only "Request failed with status code 400"; the Admin API
 * puts the reason a change was refused in the body as `{ message }`.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: string; Message?: string } } })?.response?.data
  return data?.message || data?.Message || fallback
}

/** A run or walk time: "1h 02m 05s", "12m 40s", "45s". */
export function formatDuration(totalSeconds?: number | null): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return '—'
  const secs = Math.max(0, Math.round(totalSeconds))
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`
  return `${s}s`
}

/** A distance from metres, in both units since admins and users are not all on one system. */
export function formatDistance(meters?: number | null): string {
  if (meters == null || !Number.isFinite(meters)) return '—'
  const miles = (meters / 1609.344).toFixed(2)
  return meters < 1000 ? `${Math.round(meters)} m (${miles} mi)` : `${(meters / 1000).toFixed(2)} km (${miles} mi)`
}
